import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import PortalModal from 'components/Shared/PortalModal'
import SearchbarInput from 'components/Shared/Searchbar'
import searchLogo from '../../../../images/icons/search_blue.svg'
import { LIBRARY_TABS } from '../../constant'
import { useLibraryFilters } from '../../hooks/useLibraryFilters'
import { mapLibraryItem } from '../../mapLibraryItem'
import FilterDropdown from '../FilterDropdown'
import { LibraryGrid } from '../LibraryList'
import { useLibraryList } from '../LibraryList/useLibraryList'
import LibraryTabs from '../LibraryTabs'
import LibraryPickerFooter from './LibraryPickerFooter'
import { useLibrarySelection } from '../../hooks/useLibrarySelection'

const noop = () => {}

export default function LibraryPickerModal({
  isOpen,
  onClose,
  onApply,
  selectionMode = 'multiple',
  title,
}) {
  const [activeTab, setActiveTab] = useState(LIBRARY_TABS.ALL)
  const scrollContainerRef = useRef(null)
  const { t } = useTranslation()
  const filters = useLibraryFilters()
  const selection = useLibrarySelection({ mode: selectionMode })
  const { selectedItems, selectedIds, createSetSelectedMedia, deselectAll } = selection
  const isSelectedTab = activeTab === LIBRARY_TABS.SELECTED

  const libraryList = useLibraryList({
    activeTab,
    search: filters.search,
    sort: filters.sort,
    ratios: filters.ratios,
    isOpen,
  })

  const mappedItems = useMemo(
    () => (libraryList.data?.items ?? []).map((apiItem) => mapLibraryItem(apiItem)),
    [libraryList.data?.items],
  )

  const gridItems = isSelectedTab ? selectedItems : mappedItems

  const setSelectedMedia = useMemo(
    () => createSetSelectedMedia(mappedItems),
    [createSetSelectedMedia, mappedItems],
  )

  const resetModalState = () => {
    filters.reset()
    deselectAll()
    setActiveTab(LIBRARY_TABS.ALL)
  }

  const handleClose = () => {
    resetModalState()
    onClose()
  }

  const handleApply = () => {
    onApply(selectedItems)
    resetModalState()
    onClose()
  }

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={handleClose}
      title={title ?? t('Library.libraryPicker.title')}
      className="library-picker-modal"
    >
      <div className="library-picker">
        <div className="library-picker__toolbar library__content-header">
          <LibraryTabs
            activeTab={activeTab}
            handleTabChange={setActiveTab}
            showSelectedTab
            selectedCount={selectedItems.length}
          />

          {!isSelectedTab && (
            <div className="right-side">
              <SearchbarInput
                defaultValue={filters.search}
                handleChange={filters.handleSearch}
                clearSearchQuery={filters.clearSearchQuery}
                setClearSearchQuery={filters.setClearSearchQuery}
                placeholder={t('Events.search.placeholder')}
                ariaLabel={t('Events.search.ariaLabel')}
                postfixView={
                  <img
                    src={searchLogo}
                    className="searchbar-comp_icon"
                    alt={t('Events.search.placeholder')}
                  />
                }
              />

              <FilterDropdown
                sort={filters.sort}
                ratios={filters.ratios}
                onSortChange={filters.handleSortChange}
                onRatioChange={filters.handleRatioChange}
                onReset={filters.handleFilterReset}
              />
            </div>
          )}
        </div>

        <div className="library-picker__content library-list" ref={scrollContainerRef}>
          <LibraryGrid
            mappedItems={gridItems}
            isLoading={!isSelectedTab && libraryList.isLoading}
            isError={!isSelectedTab && libraryList.isError}
            isFetchingMore={!isSelectedTab && libraryList.isFetchingMore}
            hasMore={!isSelectedTab && libraryList.hasMore}
            isFetchMoreError={!isSelectedTab && libraryList.isFetchMoreError}
            hasFilters={!isSelectedTab && libraryList.hasFilters}
            fetchNextPage={isSelectedTab ? noop : libraryList.fetchNextPage}
            retryFetchMore={isSelectedTab ? noop : libraryList.retryFetchMore}
            selectiveMode
            selectedMedia={selectedIds}
            setSelectedMedia={setSelectedMedia}
            emptyDescription={isSelectedTab ? t('Library.libraryPicker.emptySelected') : undefined}
            intersectionRootRef={scrollContainerRef}
          />
        </div>

        <LibraryPickerFooter
          selectedCount={selectedItems.length}
          onDeselectAll={deselectAll}
          onApply={handleApply}
        />
      </div>
    </PortalModal>
  )
}

LibraryPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  selectionMode: PropTypes.oneOf(['single', 'multiple']),
  title: PropTypes.string,
}
